import { createPortal } from "react-dom";

import { ModalBase } from "./ModalBase";

import type { ModalWidth } from "./ModalBase";
import type { PropsWithChildren, ReactNode } from "react";

import * as styles from "./modal.scss";

function getModalRoot(): HTMLElement {
	const modalId = "modal-root";
	const root = document.getElementById(modalId);
	if (!root) {
		throw new Error(
			`Modal root element with id "${modalId}" not found. Please ensure that there is a div with id "${modalId}" in your HTML.`,
		);
	}
	root.classList.add(styles.modalLayer);
	return root;
}

export enum ModalCloseReason {
	/** If the user closed the dialog with the esc key */
	Key,
	/** If the user closed the dialog by clicking the background  */
	Background,
	/** If the user closed the dialog by clicking the cross button */
	Button,
}

export interface ModalProps {
	visible: boolean;
	/**
	 * When `true`, ignore any modal actions like open and close.
	 */
	disabled?: boolean;
	disableBackgroundClick?: boolean;
	noHeader?: boolean;
	title?: ReactNode;
	width?: ModalWidth;
	testId?: string;
	onClose: (reason: ModalCloseReason) => void;
	/**
	 * When `true`, the first focusable element in modal will be automatically focused on modal mount.
	 *
	 * When a CSS selector (string) is passed, that element will be automatically focused on modal mount
	 *
	 * Defaults to `true`.
	 */
	autoFocus?: boolean | string;
	/**
	 * @deprecated Should only be set to `true` when using with deprecated modal hooks (`useModalStack`, `useModalsAsync`).
	 */
	alwaysMounted?: boolean;
}

export const Modal = ({
	onClose,
	visible,
	title,
	noHeader,
	children,
	disableBackgroundClick,
	disabled,
	width,
	testId,
	autoFocus = true,
	alwaysMounted = false,
}: PropsWithChildren<ModalProps>) => {
	const portalTarget = getModalRoot();

	const handleClose = (reason: ModalCloseReason) => {
		!disabled && visible && onClose(reason);
	};

	const modalBaseProps = {
		alwaysMounted,
		visible,
		onBackgroundClick: () => !disableBackgroundClick && handleClose(ModalCloseReason.Background),
		onCloseButtonClick: () => handleClose(ModalCloseReason.Button),
		onEscape: () => handleClose(ModalCloseReason.Key),
		autoFocus,
		...(width !== undefined && { width }),
		...(testId !== undefined && { testId }),
		...(title !== undefined && !noHeader && { title }),
		...(disabled && { disableEasyClosing: true }),
	};

	return createPortal(
		<ModalBase {...modalBaseProps}>
			{children}
		</ModalBase>,
		portalTarget,
	);
};
